import type { Identifier } from "ra-core";

import {
  orderActivitiesForStage,
  renderMessageTemplate,
  type MessagePlaceholderValues,
} from "../activityLog/activityLogRules";
import type {
  Deal,
  DealActivityResult,
  DealActivityType,
  DealStageActivity,
  Task,
} from "../types";

/**
 * Almikia Epic EW1 (TASK-124), ticket rules 2 and 5: the shared "Enviar
 * WhatsApp" action's message-precedence logic -- pure, no I/O, no React, so
 * every wave-3 placement (TASK-125 Hoy, TASK-126 lead show, TASK-127 contact
 * show) gets the identical prefilled text without duplicating this chain.
 * `WhatsAppAction.tsx` is the only caller; it feeds this module data it has
 * already fetched from cached catalogue queries (NFR: resolved before the
 * tap, never per-card).
 */

/** The two EW1 catalogue rows seeded by TASK-123 (`ADR-...-TASK-123`) -- looked up by their immutable `key`, never by name/label. */
export const WHATSAPP_ACTIVITY_KEY = "contacto_whatsapp";
export const WHATSAPP_RESULT_KEY = "whatsapp_mensaje_abierto";

/** The exact text `log_deal_activity()` writes for an activity/result pair (`activityCatalogueProvider.ts`), reused verbatim so the tap's guarded insert produces the identical snapshot format. */
export function formatWhatsAppLogText(
  activityName: string,
  resultLabel: string,
): string {
  return `${activityName} · ${resultLabel}`;
}

/**
 * Ticket rule 2's final fallback: `Hola {nombre}, soy {asesora} de Almikia.`
 * ASSUMPTION (no owner wording given for a missing value): degrades to
 * "Hola" / "soy tu asesora de Almikia." so the rendered text never carries a
 * literal `{nombre}`/`{asesora}` placeholder (acceptance criterion 5).
 */
export function buildWhatsAppGreeting(
  values: MessagePlaceholderValues,
): string {
  const nombre = values.nombre?.trim();
  const asesora = values.asesora?.trim();
  const greeting = nombre ? `Hola ${nombre}` : "Hola";
  const signature = asesora
    ? `soy ${asesora} de Almikia.`
    : "soy tu asesora de Almikia.";
  return `${greeting}, ${signature}`;
}

const UNFILLED_PLACEHOLDER_PATTERN = /\{(nombre|asesora|proyecto)\}/;

/** A template candidate is skipped when archived, empty, or when a placeholder it needs cannot be filled (ticket rule 2/4). */
type WhatsAppTemplateCandidate =
  | Pick<DealActivityType, "message_template" | "archived_at">
  | null
  | undefined;

/**
 * Ticket rules 2/5, first match wins: renders each candidate and returns the
 * first one whose rendered text carries no unfilled placeholder, falling
 * back to the built-in greeting when none qualifies.
 */
export function resolveWhatsAppMessage(
  candidates: readonly WhatsAppTemplateCandidate[],
  values: MessagePlaceholderValues,
): string {
  for (const candidate of candidates) {
    if (!candidate || candidate.archived_at != null) continue;
    if (!candidate.message_template) continue;
    const rendered = renderMessageTemplate(candidate.message_template, values);
    if (!UNFILLED_PLACEHOLDER_PATTERN.test(rendered)) return rendered;
  }
  return buildWhatsAppGreeting(values);
}

/** Ticket rule 2(a): the activity of the lead's OPEN next-step task (`activity_type_id` set, `done_date` NULL), if any. */
export function findOpenNextStepActivity(
  openTasks: readonly Pick<Task, "activity_type_id" | "done_date">[],
  activityTypes: readonly DealActivityType[],
): DealActivityType | null {
  const openTask = openTasks.find(
    (task) => task.activity_type_id != null && task.done_date == null,
  );
  if (!openTask) return null;
  return (
    activityTypes.find(
      (activity) => activity.id === openTask.activity_type_id,
    ) ?? null
  );
}

/** Ticket rule 2(b): the first catalogue activity linked to `stageKey`, in the same "De esta etapa" order `orderActivitiesForStage` gives "Registrar actividad". */
export function findFirstStageActivity(
  activityTypes: readonly DealActivityType[],
  stageActivities: readonly DealStageActivity[],
  stageKey: string | null | undefined,
): DealActivityType | null {
  const groups = orderActivitiesForStage(
    activityTypes,
    stageActivities,
    stageKey,
  );
  const linked = groups.find((group) => group.label === "De esta etapa");
  return linked?.activities[0] ?? null;
}

/** Ticket rule 2(c)/5: the seeded "Contacto por WhatsApp" activity, by its immutable key. */
export function findWhatsAppActivity(
  activityTypes: readonly DealActivityType[],
): DealActivityType | null {
  return (
    activityTypes.find((activity) => activity.key === WHATSAPP_ACTIVITY_KEY) ??
    null
  );
}

/** The seeded "Mensaje abierto" result for the WhatsApp activity, by its immutable key. */
export function findWhatsAppResult(
  activityResults: readonly DealActivityResult[],
  whatsappActivityId: Identifier | null | undefined,
): DealActivityResult | null {
  if (whatsappActivityId == null) return null;
  return (
    activityResults.find(
      (result) =>
        result.key === WHATSAPP_RESULT_KEY &&
        result.activity_type_id === whatsappActivityId,
    ) ?? null
  );
}

/**
 * Ticket rule 2, full precedence (a) -> (b) -> (c) -> (d) for a KNOWN lead:
 * the open next-step task's activity, then the lead's stage activity, then
 * the seeded WhatsApp activity, then the built-in greeting.
 */
export function resolveLeadWhatsAppMessage(input: {
  deal: Pick<Deal, "stage">;
  openTasksForDeal: readonly Pick<Task, "activity_type_id" | "done_date">[];
  activityTypes: readonly DealActivityType[];
  stageActivities: readonly DealStageActivity[];
  whatsappActivity: DealActivityType | null;
  values: MessagePlaceholderValues;
}): string {
  const openActivity = findOpenNextStepActivity(
    input.openTasksForDeal,
    input.activityTypes,
  );
  const stageActivity = findFirstStageActivity(
    input.activityTypes,
    input.stageActivities,
    input.deal.stage,
  );
  return resolveWhatsAppMessage(
    [openActivity, stageActivity, input.whatsappActivity],
    input.values,
  );
}

/** Ticket rule 2/5, "when no lead is involved": (c) -> (d) only. */
export function resolveGenericWhatsAppMessage(input: {
  whatsappActivity: DealActivityType | null;
  values: MessagePlaceholderValues;
}): string {
  return resolveWhatsAppMessage([input.whatsappActivity], input.values);
}

export type WhatsAppLeadChoice =
  | { kind: "none" }
  | { kind: "single"; deal: Deal }
  | { kind: "choose"; deals: Deal[] };

/**
 * Ticket rule 5, contact-only lead choice, given the contact's non-archived
 * leads the user can already write (writability itself needs the async
 * `deal_notes` create gate, checked by the caller before this pure step):
 * none logs nowhere, exactly one is auto-picked, several need the chooser.
 */
export function chooseWhatsAppLead(
  writableDeals: readonly Deal[],
): WhatsAppLeadChoice {
  if (writableDeals.length === 0) return { kind: "none" };
  if (writableDeals.length === 1)
    return { kind: "single", deal: writableDeals[0] };
  return { kind: "choose", deals: [...writableDeals] };
}
